import {
    SidebarGroup,
    SidebarGroupContent,
    SidebarMenu,
    SidebarMenuBadge,
    SidebarMenuButton,
    SidebarMenuItem,
    SidebarMenuSub,
    SidebarMenuSubButton,
} from '@duck/ui'
import React from 'react'
import { Link } from '@tanstack/react-router'
import { Home, Trophy, Activity, Info, HelpCircle, User, ClipboardList } from 'lucide-react'
import { useTar } from '@/modules/i18n/useTar'
import { useOngoingChallenges } from '@/hooks/useOngoingChallenges'
import { SideBarAdmin } from './SideBarAdmin'
import { SideBarPartner } from './SideBarPartner'
import { useAuth } from '@/hooks/useAuth'
import { ChallengeService } from '@duck/api-definitions'

type SideBarMenuProps = {
    open: boolean
    handleMobileNav: () => void
}

const activeAssignmentStatuses: ChallengeService.AvailableChallengeStatusEnum[] = ['available', 'ongoing', 'unbooted']

export const SideBarMenu = ({ open, handleMobileNav }: SideBarMenuProps) => {
    const { t } = useTar()
    const { challenges: ongoingChallenges, assignments } = useOngoingChallenges()
    const { isAdmin, isPartner, isPerson, isAuthenticated } = useAuth()

    const activeAssignments = assignments.filter(assignment => activeAssignmentStatuses.includes(assignment.status)
        && (!assignment.endDate || new Date(assignment.endDate) > new Date()))
    const ongoingAssignments = activeAssignments.filter(assignment => assignment.slug)

    return (
        <>
            {/* Main Navigation Section */}
            <SidebarGroup>
                <SidebarGroupContent>
                    <SidebarMenu>
                        <SidebarMenuItem>
                            <SidebarMenuButton asChild>
                                <Link to="/" onClick={handleMobileNav}>
                                    <Home />
                                    <span>{t('common.home')}</span>
                                </Link>
                            </SidebarMenuButton>
                        </SidebarMenuItem>
                        {isAuthenticated && <SidebarMenuItem>
                            <SidebarMenuButton asChild>
                                <Link to="/user-area" onClick={handleMobileNav}>
                                    <User />
                                    <span>{t('common.userArea')}</span>
                                </Link>
                            </SidebarMenuButton>
                        </SidebarMenuItem>}
                        {isPerson && activeAssignments.length > 0 && <SidebarMenuItem>
                            <SidebarMenuButton asChild>
                                <Link to="/assignments" onClick={handleMobileNav}>
                                    <ClipboardList />
                                    <span>{t('partner.assignments')}</span>
                                    <SidebarMenuBadge>{activeAssignments.length}</SidebarMenuBadge>
                                </Link>
                            </SidebarMenuButton>
                            {ongoingAssignments.length > 0 && <SidebarMenuSub>
                                {ongoingAssignments.map(assignment => assignment.slug && (
                                    <SidebarMenuSubButton key={assignment.assignmentId} asChild>
                                        <Link to="/challenge/$slug" params={{ slug: assignment.slug }} onClick={handleMobileNav}>
                                            <span>{assignment.name}</span>
                                        </Link>
                                    </SidebarMenuSubButton>
                                ))}
                            </SidebarMenuSub>}
                        </SidebarMenuItem>}
                        <SidebarMenuItem>
                            {!open ? (
                                <SidebarMenuButton asChild>
                                    <Link to="/challenges/available" onClick={handleMobileNav}>
                                        <Trophy />
                                        <span>{t('navigation.challenges')}</span>
                                    </Link>
                                </SidebarMenuButton>
                            ) : (
                                <>
                                    <SidebarMenuButton asChild>
                                        <div className="pointer-events-none">
                                            <Trophy />
                                            <span>{t('navigation.challenges')}</span>
                                        </div>
                                    </SidebarMenuButton>
                                    <SidebarMenuSub>
                                        <SidebarMenuSubButton asChild>
                                            <Link to="/challenges/available" onClick={handleMobileNav}>
                                                <span>{t('challenges.available')}</span>
                                            </Link>
                                        </SidebarMenuSubButton>
                                        <SidebarMenuSubButton asChild>
                                            <Link to="/challenges/completed" onClick={handleMobileNav}>
                                                <span>{t('challenges.completedStatus')}</span>
                                            </Link>
                                        </SidebarMenuSubButton>
                                    </SidebarMenuSub>
                                </>
                            )}
                        </SidebarMenuItem>
                        {ongoingChallenges.length > 0 && (
                            <SidebarMenuItem>
                                {!open ? (
                                    <SidebarMenuButton asChild>
                                        <Link
                                            to="/challenge/$slug"
                                            params={{ slug: ongoingChallenges[0].slug }}
                                            onClick={handleMobileNav}
                                        >
                                            <Activity />
                                            <span>{t('navigation.ongoingChallenges')}</span>
                                            <SidebarMenuBadge>{ongoingChallenges.length}</SidebarMenuBadge>
                                        </Link>
                                    </SidebarMenuButton>
                                ) : (
                                    <>
                                        <SidebarMenuButton asChild>
                                            <div className="pointer-events-none">
                                                <Activity />
                                                <span>{t('navigation.ongoingChallenges')}</span>
                                                <SidebarMenuBadge>{ongoingChallenges.length}</SidebarMenuBadge>
                                            </div>
                                        </SidebarMenuButton>
                                        <SidebarMenuSub>
                                            {ongoingChallenges.map((challenge) => (
                                                <SidebarMenuSubButton key={challenge.challengeId || challenge.slug} asChild>
                                                    <Link to="/challenge/$slug" params={{ slug: challenge.slug }} onClick={handleMobileNav}>
                                                        <span>{challenge.name}</span>
                                                    </Link>
                                                </SidebarMenuSubButton>
                                            ))}
                                        </SidebarMenuSub>
                                    </>
                                )}
                            </SidebarMenuItem>
                        )}
                        <SidebarMenuItem>
                            <SidebarMenuButton asChild>
                                <Link to="/about" onClick={handleMobileNav}>
                                    <Info />
                                    <span>{t('navigation.aboutUs')}</span>
                                </Link>
                            </SidebarMenuButton>
                        </SidebarMenuItem>
                        <SidebarMenuItem>
                            <SidebarMenuButton asChild>
                                <Link to="/faq" onClick={handleMobileNav}>
                                    <HelpCircle />
                                    <span>{t('navigation.faq')}</span>
                                </Link>
                            </SidebarMenuButton>
                        </SidebarMenuItem>
                    </SidebarMenu>
                </SidebarGroupContent>
            </SidebarGroup>

            {isPartner && <SideBarPartner open={open} handleMobileNav={handleMobileNav} />}

            {isAdmin && <SideBarAdmin open={open} handleMobileNav={handleMobileNav} />}
        </>
    )
}
